/**
 * Firewall panel for the resource detail page's Networking tab (VMs only).
 *
 * Admin counterpart of the client console's `ResourceFirewallTab`
 * (`wi-cloud-client/src/components/resources/detail/ResourceFirewallTab.tsx`): same table shape,
 * same rule form, same validation. Two differences are deliberate:
 *
 * - The rules load when the panel mounts, and it only mounts with the Networking tab open (Radix
 *   unmounts an inactive tab's content). Every read reaches the hypervisor through WiService (n8n),
 *   so the query is gated on that tab rather than fired with the page.
 * - Writes are SuperAdmin only. A SupportAdmin can load, search, filter and read the rules, options
 *   and logs, but sees no Add/Edit/Delete button and no way to flip the global enable switch or an
 *   option select — those controls are not rendered at all for that role, not merely disabled.
 *
 * There is no expiry gate: unlike the client console, the admin API lets an operator act on an
 * expired VM, so writes here are only blocked by role and by an in-flight save.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AddressInput, PortInput, RuleSide } from "./FirewallValueInput";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronsUpDown,
  Edit,
  FileText,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Shield,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { adminResourceService } from "@/services/admin-resource.service";
import {
  resourceKeys,
  useFirewallMacros,
  useResourceFirewall,
  useResourceFirewallLogs,
} from "@/hooks/useResourceQueries";
import { usePermissions } from "@/hooks/usePermissions";
import {
  BusyOverlay,
  PROVIDER_WAIT_HINT,
} from "@/components/common/BusyOverlay";
import {
  ANY_PROTOCOL,
  COMMON_PROTOCOLS,
  DEFAULT_FIREWALL_RULE_FORM,
  MAX_COMMENT_LENGTH,
  OTHER_PROTOCOLS,
  RULE_TOOLBAR_THRESHOLD,
  buildRuleUpdate,
  filterFirewallRules,
  isAll,
  stripProtoWhenMacroSet,
  validateFirewallRule,
  type FirewallRuleField,
  type RuleDirectionFilter,
} from "@/lib/firewallRules";
import type {
  CreateFirewallRuleRequest,
  FirewallOptions,
  FirewallRule,
} from "@/types/admin";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface ResourceFirewallPanelProps {
  resourceId: string;
}

/** The rule table's columns, in order; the <colgroup> is generated from this list. */
const RULE_COLUMNS = [
  "pos",
  "direction",
  "action",
  "protocol",
  "source",
  "dest",
  "dport",
  "sport",
  "comment",
  "actions",
] as const;

type RuleColumn = (typeof RULE_COLUMNS)[number];

/**
 * Sized to what each column carries: a full "first-last" IP range under Source/Destination, a
 * short port list under the ports. The sum exceeds a narrow panel on purpose — the box scrolls
 * sideways with the actions pinned, rather than truncating every value.
 */
const RULE_COLUMN_WIDTHS: Record<RuleColumn, string> = {
  pos: "48px",
  direction: "64px",
  action: "112px",
  protocol: "120px",
  source: "220px",
  dest: "220px",
  dport: "120px",
  sport: "112px",
  comment: "200px",
  actions: "88px",
};

const RULE_HEAD_CLASS =
  "h-10 bg-panel px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground";

const RULE_DIRECTION_OPTIONS: { value: RuleDirectionFilter; label: string }[] =
  [
    { value: "all", label: "All" },
    { value: "in", label: "In" },
    { value: "out", label: "Out" },
  ];

/**
 * Every log level WiService takes for `log_level_in` / `log_level_out`, least to most severe after
 * "no log". A VM set to any of them must show it; a shorter list left the select blank for one set
 * to `debug` or `crit`.
 */
const LOG_LEVELS = [
  { value: "nolog", label: "No Log" },
  { value: "debug", label: "Debug" },
  { value: "info", label: "Info" },
  { value: "notice", label: "Notice" },
  { value: "warning", label: "Warning" },
  { value: "err", label: "Error" },
  { value: "crit", label: "Critical" },
  { value: "alert", label: "Alert" },
  { value: "emerg", label: "Emergency" },
] as const;

/** The protocol cell: the macro when there is one, else the protocol, or "All protocols" when blank. */
function ruleProtocolLabel(rule: FirewallRule): string {
  if (!isAll(rule.macro)) return rule.macro!;
  if (isAll(rule.proto)) return "All protocols";
  return rule.proto!;
}

/**
 * One value in the rule table. Blank reads "all" so the row states everything the rule matches; a
 * long list or range is truncated with the full value on hover. Under a macro the ports are the
 * macro's, so the cell shows a dash rather than a misleading "all".
 */
function RuleCellValue({
  value,
  macroManaged = false,
}: {
  value?: string;
  macroManaged?: boolean;
}) {
  if (macroManaged) {
    return (
      <span className="text-sm text-muted-foreground" title="Set by macro">
        —
      </span>
    );
  }

  if (isAll(value)) {
    return <span className="text-sm text-muted-foreground">all</span>;
  }

  return (
    <p className="truncate font-mono text-sm text-foreground" title={value}>
      {value}
    </p>
  );
}

/** The validation message under a rule field; renders nothing while the field is valid. */
function FieldError({
  id,
  message,
  className,
}: {
  id: string;
  message?: string;
  className?: string;
}) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      className={cn("text-sm text-destructive", className)}
    >
      {message}
    </p>
  );
}

/**
 * Searchable macro picker for the rule form. Same shape as `AuditActionCombobox`
 * (`components/audit/AuditActionCombobox.tsx`) — a Popover with a plain filtered list, not the
 * `cmdk`-backed `Command` primitive the client console uses (admin's ui library has no `Command`
 * wrapper) — and the same strict substring match: macro descriptions are long sentences, and fuzzy
 * scoring would make almost all of them match any short query.
 */
function MacroCombobox({
  id,
  value,
  macros,
  disabled,
  onSelect,
}: {
  id: string;
  value: string;
  macros: { macro: string; desc: string }[];
  disabled?: boolean;
  onSelect: (macro: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return macros;
    return macros.filter(
      (m) =>
        m.macro.toLowerCase().includes(term) ||
        m.desc.toLowerCase().includes(term),
    );
  }, [macros, search]);

  const select = (macro: string) => {
    onSelect(macro);
    setOpen(false);
    setSearch("");
  };

  // `modal`: the picker sits in the rule Dialog, whose scroll lock swallows wheel events on
  // anything outside it — and the list is portalled to <body>. A modal Popover takes the lock
  // over while open, so its own list scrolls.
  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between bg-transparent font-normal",
            !value && "text-placeholder",
          )}
        >
          <span className="truncate">{value || "Select a macro…"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        <div className="border-b p-2">
          <Input
            placeholder="Search macros…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
        </div>
        <div className="scrollbar-thin max-h-64 overflow-auto p-1">
          <button
            type="button"
            className={cn(
              "flex w-full cursor-pointer items-center justify-between rounded-sm px-3 py-2 text-left text-sm hover:bg-accent",
              !value && "bg-accent",
            )}
            onClick={() => select("")}
          >
            <span>None</span>
            {!value && <Check className="h-4 w-4 shrink-0" />}
          </button>
          {matches.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No macro found.
            </div>
          )}
          {matches.map((m) => (
            <button
              key={m.macro}
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-start justify-between gap-2 rounded-sm px-3 py-2 text-left hover:bg-accent",
                m.macro === value && "bg-accent",
              )}
              onClick={() => select(m.macro)}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">
                  {m.macro}
                </span>
                {m.desc && (
                  <span className="block truncate text-xs text-muted-foreground">
                    {m.desc}
                  </span>
                )}
              </span>
              {m.macro === value && (
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Direction/action/macro/protocol/ports/addresses/comment fields shared by the Add and Edit
 * dialogs. `baseline` is only set for the Edit dialog — see `validateFirewallRule`.
 */
function RuleForm({
  form,
  setForm,
  macros,
  showEnableToggle = false,
  baseline,
  onBlockedChange,
}: {
  form: CreateFirewallRuleRequest;
  setForm: (f: CreateFirewallRuleRequest) => void;
  macros: { macro: string; desc: string }[];
  showEnableToggle?: boolean;
  baseline?: CreateFirewallRuleRequest;
  /**
   * Told whenever a range or list field holds something unfinished or wrong that the string checks
   * cannot see (a kind chosen but left empty would read as "all"), so Save waits for it.
   */
  onBlockedChange?: (blocked: boolean) => void;
}) {
  const id = useId();
  const [blockedFields, setBlockedFields] = useState<Record<string, boolean>>(
    {},
  );
  const reportProblem = (field: FirewallRuleField) => (blocked: boolean) =>
    setBlockedFields((current) =>
      current[field] === blocked ? current : { ...current, [field]: blocked },
    );
  const anyBlocked = Object.values(blockedFields).some(Boolean);
  const reportBlockedRef = useRef(onBlockedChange);
  reportBlockedRef.current = onBlockedChange;
  useEffect(() => {
    reportBlockedRef.current?.(anyBlocked);
  }, [anyBlocked]);
  useEffect(() => () => reportBlockedRef.current?.(false), []);

  const macroManaged = !isAll(form.macro);
  const selectedMacro = macros.find((m) => m.macro === form.macro);
  const errors: Partial<Record<FirewallRuleField, string>> =
    validateFirewallRule(form, baseline);
  const protocolRequired =
    !macroManaged && (!isAll(form.sport) || !isAll(form.dport));

  return (
    <div className="grid gap-3 py-2">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-direction`}>Direction *</Label>
          <Select
            value={form.type}
            onValueChange={(v: "in" | "out") => setForm({ ...form, type: v })}
          >
            <SelectTrigger id={`${id}-direction`} className="cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="in" className="cursor-pointer">
                Inbound (IN)
              </SelectItem>
              <SelectItem value="out" className="cursor-pointer">
                Outbound (OUT)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-action`}>Action *</Label>
          <Select
            value={form.action}
            onValueChange={(v: "ACCEPT" | "DROP" | "REJECT") =>
              setForm({ ...form, action: v })
            }
          >
            <SelectTrigger id={`${id}-action`} className="cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACCEPT" className="cursor-pointer">
                ACCEPT
              </SelectItem>
              <SelectItem value="DROP" className="cursor-pointer">
                DROP
              </SelectItem>
              <SelectItem value="REJECT" className="cursor-pointer">
                REJECT
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${id}-macro`}>Macro (optional)</Label>
        <MacroCombobox
          id={`${id}-macro`}
          value={form.macro ?? ""}
          macros={macros}
          onSelect={(macro) =>
            setForm(
              macro
                ? { ...form, macro, proto: "", sport: "", dport: "" }
                : { ...form, macro: "", proto: "" },
            )
          }
        />
        {selectedMacro?.desc && (
          <p className="text-sm text-muted-foreground">{selectedMacro.desc}</p>
        )}
      </div>

      <div className="grid gap-3">
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-proto`}>
            {protocolRequired ? "Protocol *" : "Protocol"}
          </Label>
          <Select
            value={macroManaged ? undefined : form.proto || ANY_PROTOCOL}
            disabled={macroManaged}
            onValueChange={(v: string) =>
              setForm({ ...form, proto: v === ANY_PROTOCOL ? "" : v })
            }
          >
            <SelectTrigger
              id={`${id}-proto`}
              className="cursor-pointer"
              aria-invalid={!!errors.proto}
              aria-describedby={errors.proto ? `${id}-proto-error` : undefined}
            >
              <SelectValue
                placeholder={macroManaged ? "Set by macro" : undefined}
              />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ANY_PROTOCOL} className="cursor-pointer">
                All protocols
              </SelectItem>
              <SelectSeparator />
              {COMMON_PROTOCOLS.map((protocol) => (
                <SelectItem
                  key={protocol}
                  value={protocol}
                  className="cursor-pointer"
                >
                  {protocol.toUpperCase()}
                </SelectItem>
              ))}
              <SelectSeparator />
              {OTHER_PROTOCOLS.map((protocol) => (
                <SelectItem
                  key={protocol}
                  value={protocol}
                  className="cursor-pointer"
                >
                  {protocol}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError id={`${id}-proto-error`} message={errors.proto} />
        </div>
        {macroManaged && (
          <p className="text-sm text-muted-foreground">
            The macro already defines the protocol and ports.
          </p>
        )}
      </div>

      <RuleSide title="Source" hint="where the traffic comes from">
        <AddressInput
          label="IP"
          value={form.source}
          error={errors.source}
          onProblemChange={reportProblem("source")}
          onChange={(source) => setForm({ ...form, source })}
        />
        <PortInput
          label="Port"
          value={form.sport}
          disabled={macroManaged}
          error={errors.sport}
          onProblemChange={reportProblem("sport")}
          onChange={(sport) => setForm({ ...form, sport })}
        />
      </RuleSide>

      <RuleSide title="Destination" hint="where the traffic goes to">
        <AddressInput
          label="IP"
          value={form.dest}
          error={errors.dest}
          onProblemChange={reportProblem("dest")}
          onChange={(dest) => setForm({ ...form, dest })}
        />
        <PortInput
          label="Port"
          value={form.dport}
          disabled={macroManaged}
          error={errors.dport}
          onProblemChange={reportProblem("dport")}
          onChange={(dport) => setForm({ ...form, dport })}
        />
      </RuleSide>

      <div className="space-y-1.5">
        <Label htmlFor={`${id}-comment`}>Comment</Label>
        <Input
          id={`${id}-comment`}
          value={form.comment || ""}
          maxLength={MAX_COMMENT_LENGTH}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
        />
      </div>

      {showEnableToggle && (
        <div className="flex items-center gap-2">
          <Switch
            id={`${id}-enable`}
            checked={form.enable === 1}
            onCheckedChange={(c) => setForm({ ...form, enable: c ? 1 : 0 })}
          />
          <Label htmlFor={`${id}-enable`} className="cursor-pointer">
            Enable Rule
          </Label>
        </div>
      )}
    </div>
  );
}

export function ResourceFirewallPanel({
  resourceId,
}: ResourceFirewallPanelProps) {
  const { isSuperAdmin } = usePermissions();
  const canWrite = isSuperAdmin();
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteRulePos, setDeleteRulePos] = useState<number | null>(null);
  const [selectedRule, setSelectedRule] = useState<FirewallRule | null>(null);
  const [editBaseline, setEditBaseline] =
    useState<CreateFirewallRuleRequest | null>(null);
  const [ruleSearch, setRuleSearch] = useState("");
  const [ruleDirection, setRuleDirection] =
    useState<RuleDirectionFilter>("all");
  const [ruleForm, setRuleForm] = useState<CreateFirewallRuleRequest>(
    DEFAULT_FIREWALL_RULE_FORM,
  );

  const {
    data: firewallData,
    isLoading: isLoadingRules,
    isFetching: isFetchingRules,
    error: rulesError,
    refetch: refetchRules,
  } = useResourceFirewall(resourceId);

  // Only a SuperAdmin can reach the rule form that needs this catalog.
  const { data: macros } = useFirewallMacros(canWrite);

  const {
    data: logs,
    isLoading: isLoadingLogs,
    refetch: refetchLogs,
  } = useResourceFirewallLogs(resourceId);

  const invalidateRules = () =>
    queryClient.invalidateQueries({
      queryKey: resourceKeys.firewall(resourceId),
    });

  const resetForm = () => {
    setRuleForm(DEFAULT_FIREWALL_RULE_FORM);
    setSelectedRule(null);
    setEditBaseline(null);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      adminResourceService.addFirewallRule(
        resourceId,
        stripProtoWhenMacroSet(ruleForm),
      ),
    onSuccess: async () => {
      await invalidateRules();
      setCreateDialogOpen(false);
      resetForm();
      toast.success("Firewall rule added.");
    },
    onError: (error: Error) =>
      toast.error(error.message || "Failed to add the firewall rule."),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (selectedRule?.pos === undefined || !editBaseline) {
        throw new Error("Rule position is missing.");
      }
      return adminResourceService.updateFirewallRule(
        resourceId,
        selectedRule.pos,
        buildRuleUpdate(ruleForm, editBaseline),
      );
    },
    onSuccess: async () => {
      await invalidateRules();
      setEditDialogOpen(false);
      resetForm();
      toast.success("Firewall rule updated.");
    },
    onError: (error: Error) =>
      toast.error(error.message || "Failed to update the firewall rule."),
  });

  const deleteMutation = useMutation({
    mutationFn: (pos: number) =>
      adminResourceService.deleteFirewallRule(resourceId, pos),
    onSuccess: async () => {
      await invalidateRules();
      setDeleteRulePos(null);
      toast.success("Firewall rule deleted.");
    },
    onError: (error: Error) =>
      toast.error(error.message || "Failed to delete the firewall rule."),
  });

  const optionsMutation = useMutation({
    mutationFn: (options: FirewallOptions) =>
      adminResourceService.updateFirewallOptions(resourceId, options),
    onSuccess: async () => {
      await invalidateRules();
      toast.success("Firewall options updated.");
    },
    onError: (error: Error) =>
      toast.error(error.message || "Failed to update firewall options."),
  });

  // Every write here replaces the VM's rule set on the provider, so the whole panel is covered
  // while one is in flight — the same reasoning as the client console's `isSaving`, and as
  // `N8nVmActionsPanel` for the other direct-to-provider writes on this page.
  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    optionsMutation.isPending;

  /** Only the request itself closes a dialog while it is pending — Escape and outside click wait. */
  const guardedOpenChange =
    (setOpen: (open: boolean) => void) => (open: boolean) => {
      if (open || !isSaving) {
        setOpen(open);
        if (!open) resetForm();
      }
    };

  const handleEditClick = (rule: FirewallRule) => {
    const initialForm: CreateFirewallRuleRequest = {
      type: rule.type,
      action: rule.action,
      enable: rule.enable,
      source: rule.source || "",
      sport: rule.sport || "",
      dest: rule.dest || "",
      dport: rule.dport || "",
      iface: rule.iface || "",
      // A rule with no protocol (a macro rule, or one that deliberately matches every protocol)
      // must keep reading that way when the dialog opens.
      proto: rule.proto || "",
      log: rule.log || "nolog",
      macro: rule.macro || "",
      comment: rule.comment || "",
    };
    setSelectedRule(rule);
    setRuleForm(initialForm);
    setEditBaseline(initialForm);
    setEditDialogOpen(true);
  };

  const applyOptionsChange = (
    key: keyof FirewallOptions,
    value: number | string,
  ) => {
    if (!firewallData?.options) return;

    // Coalesce with the same defaults the controls below display — the endpoint requires every
    // field on a write (it can no longer tell "not set" from "explicitly cleared"), so toggling
    // just one control must still submit real values for the untouched ones.
    const current = firewallData.options;
    const next: FirewallOptions = {
      enable: current.enable,
      policyIn: current.policyIn || "DROP",
      policyOut: current.policyOut || "ACCEPT",
      logLevelIn: current.logLevelIn || "nolog",
      logLevelOut: current.logLevelOut || "nolog",
      digest: current.digest,
      [key]: value,
    };
    optionsMutation.mutate(next);
  };

  // Set by the open RuleForm: a range or list field holds something the string checks below
  // cannot judge, such as a kind chosen but left empty.
  const [ruleFormBlocked, setRuleFormBlocked] = useState(false);
  const ruleFormInvalid =
    ruleFormBlocked ||
    Object.keys(
      validateFirewallRule(
        ruleForm,
        editDialogOpen ? (editBaseline ?? undefined) : undefined,
      ),
    ).length > 0;

  const rules = firewallData?.rules ?? [];
  const showRuleToolbar = rules.length >= RULE_TOOLBAR_THRESHOLD;
  const visibleRules = showRuleToolbar
    ? filterFirewallRules(rules, ruleSearch, ruleDirection)
    : rules;
  const isLastRule = rules.length === 1;
  const options: FirewallOptions = firewallData?.options || { enable: 0 };

  return (
    <Card className="border-border/80 shadow-none">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Firewall
          </CardTitle>
          <CardDescription>
            Live rules, policies and logs from the hypervisor.
          </CardDescription>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title="Reload firewall rules"
          disabled={isFetchingRules || isSaving}
          onClick={() => void refetchRules()}
        >
          <RefreshCw
            className={cn("h-4 w-4", isFetchingRules && "animate-spin")}
          />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {rulesError && !firewallData ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load the firewall configuration</AlertTitle>
            <AlertDescription>
              {rulesError instanceof Error
                ? rulesError.message
                : "The admin API could not reach WiService for this VM."}
              <div className="mt-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void refetchRules()}
                  disabled={isFetchingRules}
                >
                  Retry
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : isLoadingRules ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : (
          <Tabs defaultValue="rules" className="space-y-4">
            {rulesError && (
              <Alert variant="destructive">
                <AlertTitle>
                  Could not refresh the firewall configuration
                </AlertTitle>
                <AlertDescription>
                  {rulesError instanceof Error
                    ? rulesError.message
                    : "The last reload failed. Showing the previously loaded rules."}
                </AlertDescription>
              </Alert>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <TabsList>
                <TabsTrigger
                  value="rules"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Shield className="h-4 w-4" /> Rules
                </TabsTrigger>
                <TabsTrigger
                  value="options"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Settings className="h-4 w-4" /> Options
                </TabsTrigger>
                <TabsTrigger
                  value="logs"
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => void refetchLogs()}
                >
                  <FileText className="h-4 w-4" /> Logs
                </TabsTrigger>
              </TabsList>

              <div className="flex flex-wrap items-center gap-2">
                <Label
                  htmlFor="firewall-enable"
                  className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium"
                >
                  Firewall
                  <Switch
                    id="firewall-enable"
                    checked={options.enable === 1}
                    disabled={!canWrite || isSaving}
                    onCheckedChange={(checked) =>
                      applyOptionsChange("enable", checked ? 1 : 0)
                    }
                  />
                </Label>

                {canWrite && (
                  <Button
                    size="sm"
                    disabled={isSaving}
                    onClick={() => {
                      resetForm();
                      setCreateDialogOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Rule
                  </Button>
                )}
                {canWrite && (
                  <Dialog
                    open={createDialogOpen}
                    onOpenChange={guardedOpenChange(setCreateDialogOpen)}
                  >
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add Firewall Rule</DialogTitle>
                      </DialogHeader>
                      <DialogBody className="space-y-3">
                        <DialogDescription>
                          Create a new firewall rule for this VM.
                        </DialogDescription>
                        <RuleForm
                          form={ruleForm}
                          setForm={setRuleForm}
                          macros={macros ?? []}
                          onBlockedChange={setRuleFormBlocked}
                        />
                      </DialogBody>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() =>
                            guardedOpenChange(setCreateDialogOpen)(false)
                          }
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => createMutation.mutate()}
                          disabled={createMutation.isPending || ruleFormInvalid}
                        >
                          Add Rule
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>

            <TabsContent value="rules" className="space-y-4">
              {rules.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
                  <div className="mb-3 rounded-lg bg-panel-2 p-2">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No firewall rules configured.
                  </p>
                </div>
              ) : (
                <>
                  {showRuleToolbar && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                      <div className="relative w-full sm:w-[360px]">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={ruleSearch}
                          onChange={(e) => setRuleSearch(e.target.value)}
                          placeholder="Search IP, port, protocol, macro or comment"
                          aria-label="Search rules"
                          className="pl-10"
                        />
                      </div>
                      <div
                        role="group"
                        aria-label="Direction"
                        className="inline-flex h-8 min-h-8 items-center rounded-md border p-0.5"
                      >
                        {RULE_DIRECTION_OPTIONS.map((option) => (
                          <Button
                            key={option.value}
                            type="button"
                            size="sm"
                            variant={
                              ruleDirection === option.value
                                ? "secondary"
                                : "ghost"
                            }
                            aria-pressed={ruleDirection === option.value}
                            className="h-7 px-3"
                            onClick={() => setRuleDirection(option.value)}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                      <span className="text-sm text-muted-foreground sm:ml-auto">
                        {visibleRules.length} of {rules.length} rules
                      </span>
                    </div>
                  )}

                  <div className="scrollbar-thin max-h-120 overflow-auto rounded-lg border [&>div]:overflow-visible">
                    <Table className="table-fixed border-collapse">
                      <colgroup>
                        {RULE_COLUMNS.map((column) => (
                          <col
                            key={column}
                            style={{ width: RULE_COLUMN_WIDTHS[column] }}
                          />
                        ))}
                      </colgroup>
                      <TableHeader className="sticky top-0 z-20">
                        <TableRow className="hover:bg-transparent">
                          <TableHead
                            className={cn(RULE_HEAD_CLASS, "text-right")}
                          >
                            #
                          </TableHead>
                          <TableHead className={RULE_HEAD_CLASS}>
                            Direction
                          </TableHead>
                          <TableHead className={RULE_HEAD_CLASS}>
                            Action
                          </TableHead>
                          <TableHead className={RULE_HEAD_CLASS}>
                            Protocol / Macro
                          </TableHead>
                          <TableHead className={RULE_HEAD_CLASS}>
                            Source
                          </TableHead>
                          <TableHead className={RULE_HEAD_CLASS}>
                            Dest.
                          </TableHead>
                          <TableHead className={RULE_HEAD_CLASS}>
                            Dest. Port
                          </TableHead>
                          <TableHead className={RULE_HEAD_CLASS}>
                            Source Port
                          </TableHead>
                          <TableHead className={RULE_HEAD_CLASS}>
                            Comment
                          </TableHead>
                          <TableHead
                            className={cn(
                              RULE_HEAD_CLASS,
                              "sticky right-0 z-30 shadow-[inset_1px_0_0_0_var(--hairline)]",
                            )}
                          >
                            <span className="sr-only">Actions</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleRules.length === 0 && (
                          <TableRow className="hover:bg-transparent">
                            <TableCell
                              colSpan={RULE_COLUMNS.length}
                              className="py-10 text-center text-sm text-muted-foreground"
                            >
                              <p>No rules match these filters.</p>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => {
                                  setRuleSearch("");
                                  setRuleDirection("all");
                                }}
                              >
                                Clear filters
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                        {visibleRules.map((rule) => {
                          const hasMacro = !isAll(rule.macro);
                          return (
                            <TableRow
                              key={rule.pos}
                              className={cn(
                                "group",
                                rule.enable === 0 &&
                                  "[&>td:not(:last-child)]:opacity-60",
                              )}
                            >
                              <TableCell className="px-3 py-2 text-right font-mono text-sm text-muted-foreground">
                                {rule.pos}
                              </TableCell>
                              <TableCell
                                className={cn(
                                  "px-3 py-2 font-mono text-sm font-medium uppercase",
                                  rule.type === "in"
                                    ? "text-info"
                                    : "text-muted-foreground",
                                )}
                              >
                                {rule.type}
                              </TableCell>
                              <TableCell className="px-3 py-2">
                                <div className="flex flex-col items-start gap-1">
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      rule.action === "ACCEPT" &&
                                        "border-ok/25 bg-ok-soft text-ok",
                                      rule.action === "DROP" &&
                                        "border-destructive/25 bg-destructive-soft text-destructive",
                                      rule.action === "REJECT" &&
                                        "border-warn/25 bg-warn-soft text-warn",
                                    )}
                                  >
                                    {rule.action}
                                  </Badge>
                                  {rule.enable === 0 && (
                                    <Badge
                                      variant="outline"
                                      className="border-hairline bg-panel-2 text-muted-foreground"
                                    >
                                      Disabled
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="px-3 py-2">
                                <code
                                  className={cn(
                                    "inline-block max-w-full truncate rounded border border-hairline bg-panel-2 px-1.5 py-0.5 align-middle text-sm",
                                    !hasMacro && "uppercase",
                                  )}
                                  title={hasMacro ? rule.macro : undefined}
                                >
                                  {ruleProtocolLabel(rule)}
                                </code>
                              </TableCell>
                              <TableCell className="px-3 py-2">
                                <RuleCellValue value={rule.source} />
                              </TableCell>
                              <TableCell className="px-3 py-2">
                                <RuleCellValue value={rule.dest} />
                              </TableCell>
                              <TableCell className="px-3 py-2">
                                <RuleCellValue
                                  value={rule.dport}
                                  macroManaged={hasMacro}
                                />
                              </TableCell>
                              <TableCell className="px-3 py-2">
                                <RuleCellValue
                                  value={rule.sport}
                                  macroManaged={hasMacro}
                                />
                              </TableCell>
                              <TableCell className="px-3 py-2">
                                {rule.comment && (
                                  <p
                                    className="truncate text-sm text-muted-foreground"
                                    title={rule.comment}
                                  >
                                    {rule.comment}
                                  </p>
                                )}
                              </TableCell>
                              <TableCell className="sticky right-0 z-10 bg-panel px-2 py-2 shadow-[inset_1px_0_0_0_var(--hairline)] transition-colors group-hover:bg-panel-2">
                                {canWrite && (
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      disabled={isSaving}
                                      onClick={() => handleEditClick(rule)}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-destructive hover:text-destructive"
                                      disabled={isSaving || isLastRule}
                                      title={
                                        isLastRule
                                          ? "The last rule cannot be deleted — the system creates an access rule again right away."
                                          : undefined
                                      }
                                      onClick={() =>
                                        setDeleteRulePos(rule.pos!)
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="options">
              <Card className="border-border/80 shadow-none">
                <CardHeader>
                  <CardTitle>Firewall Options</CardTitle>
                  <CardDescription>
                    Global firewall policies and logging.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Input Policy</Label>
                      <Select
                        value={options.policyIn || "DROP"}
                        disabled={!canWrite || isSaving}
                        onValueChange={(v) => applyOptionsChange("policyIn", v)}
                      >
                        <SelectTrigger className="cursor-pointer">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ACCEPT" className="cursor-pointer">
                            ACCEPT
                          </SelectItem>
                          <SelectItem value="DROP" className="cursor-pointer">
                            DROP
                          </SelectItem>
                          <SelectItem value="REJECT" className="cursor-pointer">
                            REJECT
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Output Policy</Label>
                      <Select
                        value={options.policyOut || "ACCEPT"}
                        disabled={!canWrite || isSaving}
                        onValueChange={(v) =>
                          applyOptionsChange("policyOut", v)
                        }
                      >
                        <SelectTrigger className="cursor-pointer">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ACCEPT" className="cursor-pointer">
                            ACCEPT
                          </SelectItem>
                          <SelectItem value="DROP" className="cursor-pointer">
                            DROP
                          </SelectItem>
                          <SelectItem value="REJECT" className="cursor-pointer">
                            REJECT
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Log Level (In)</Label>
                      <Select
                        value={options.logLevelIn || "nolog"}
                        disabled={!canWrite || isSaving}
                        onValueChange={(v) =>
                          applyOptionsChange("logLevelIn", v)
                        }
                      >
                        <SelectTrigger className="cursor-pointer">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LOG_LEVELS.map((level) => (
                            <SelectItem
                              key={level.value}
                              value={level.value}
                              className="cursor-pointer"
                            >
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Log Level (Out)</Label>
                      <Select
                        value={options.logLevelOut || "nolog"}
                        disabled={!canWrite || isSaving}
                        onValueChange={(v) =>
                          applyOptionsChange("logLevelOut", v)
                        }
                      >
                        <SelectTrigger className="cursor-pointer">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LOG_LEVELS.map((level) => (
                            <SelectItem
                              key={level.value}
                              value={level.value}
                              className="cursor-pointer"
                            >
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="logs">
              <Card className="border-border/80 shadow-none">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Firewall Logs</CardTitle>
                    <CardDescription>Recent firewall activity.</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void refetchLogs()}
                    disabled={isLoadingLogs}
                  >
                    <RefreshCw
                      className={cn(
                        "mr-2 h-4 w-4",
                        isLoadingLogs && "animate-spin",
                      )}
                    />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {isLoadingLogs ? (
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  ) : !logs || logs.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      No logs available.
                    </div>
                  ) : (
                    <div className="bg-muted/50 h-100 overflow-y-auto rounded-md p-4 font-mono text-sm">
                      {logs.map((log) => (
                        <div
                          key={log.n}
                          className="whitespace-pre-wrap border-b border-border/50 py-1 last:border-0"
                        >
                          {log.t}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>

      {canWrite && (
        <Dialog
          open={editDialogOpen}
          onOpenChange={guardedOpenChange(setEditDialogOpen)}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Firewall Rule</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-3">
              <DialogDescription>
                Leave a field blank to allow any address, port or protocol;
                clearing a field removes that condition from the rule.
              </DialogDescription>
              <RuleForm
                form={ruleForm}
                setForm={setRuleForm}
                macros={macros ?? []}
                baseline={editBaseline ?? undefined}
                showEnableToggle
                onBlockedChange={setRuleFormBlocked}
              />
            </DialogBody>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => guardedOpenChange(setEditDialogOpen)(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => updateMutation.mutate()}
                disabled={updateMutation.isPending || ruleFormInvalid}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canWrite && (
        <AlertDialog
          open={deleteRulePos !== null}
          onOpenChange={(open) => {
            if (open || !isSaving)
              setDeleteRulePos(open ? deleteRulePos : null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Firewall Rule?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogBody>
              <AlertDialogDescription>
                This will remove the firewall rule at position #
                {deleteRulePos ?? ""}. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogBody>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isSaving}
                onClick={(event) => {
                  event.preventDefault();
                  if (deleteRulePos !== null) {
                    deleteMutation.mutate(deleteRulePos);
                  }
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <BusyOverlay
        open={isSaving}
        label="Saving firewall change…"
        hint={PROVIDER_WAIT_HINT}
      />
    </Card>
  );
}
